import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { noop, isEmpty, get, map, size } from 'lodash';
import moment from 'moment';

// local dependencies
import { Icon } from 'modules/icon-bank';
import { popover } from 'modules/popover';
import { calendarRange as CalendarRange } from 'modules/calendarRange';
import { radioBox as RadioBox } from 'ui/views/generic/radioBox';
import { getLabel } from 'utils/getLabel';

/************************************************/

// calendar view
const CalendarView = ( props ) => {

    // check if frequency selection is allowed
    let frequencyConfig = get( props, 'config.frequency', undefined );
    
    if( 0 !== size( get( props, 'config.options', [] ) ) ) {
        frequencyConfig = get( props, 'config', undefined );
    }

    const [ frequency, updateFrequency ] = useState( props.frequency );

    return (
        <>
            { 0 !== size( get( frequencyConfig, 'options', [] ) ) && (

                <div className='ui-calendar-range__options'>

                    { map( get( frequencyConfig, 'options', [] ), option => {
                        return(
                            <div key={ option.value } className='ui-calendar-range__options__option'
                            onClick={ () => { updateFrequency( option.value ); props.onFrequencyChange( option.value ); } }>
                                <RadioBox selected={ frequency === option.value } name={ option.name } />
                                { getLabel( option.title ) }
                            </div>
                        );
                    } ) }
                </div>
            ) }
           
            <CalendarRange
                className='ui-calendar-range__calendar'
                value={ props.value }
                selectRange={ true }
                allowPartialRange={ true }
                onChange={ ( selection ) => {
                    props.onChange( selection );
                } }
                disableMaxDate={ get( props, 'config.disableMaxDate', false ) }
            />
        </>
    );
};

// set display name
CalendarView.displayName = 'dateRangePicker.CalendarView';

/*---------*/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { dateRangePicker as DateRangePicker } from 'ui/views/generic/dateRangePicker';
 *
 * // render
 * <DateRangePicker propName='propValue'/>
 */
export const dateRangePicker = ( props ) => {

    // widget class name
    const widgetClassName = classnames( 'ui-date-range-picker', props.className );

    //Dates Extracting and checking if empty then select 1st date of the current month to current Date
    const dateRangeValue = [ ...props.value ];
    isEmpty( dateRangeValue )
        ? dateRangeValue.push(
                moment(
                    new Date( new Date().getFullYear(), new Date().getMonth(), 1 )
                ).format( 'MM-DD-YYYY' ),
                moment( new Date() ).format( 'MM-DD-YYYY' )
            )
        : null;

    // open popover datepicker
    const openPopover = useCallback( ( element ) => {
        popover.open( {
            referenceElement: element,
            view: CalendarView,
            tooltip: false,
            props: {
                config: props.config,
                
                // options
                value: dateRangeValue,
                onChange: props.onChange,
                frequency: props.frequency,
                onFrequencyChange: props.onFrequencyChange,
                closeOnSelection: props.closeOnSelection,
                className: 'ui-date-range-picker__calendar'
            },
            styles: {
                backgroundColor: 'transparent',
            }
        } );
    }, [ props.value, props.frequency ] );

    return (
        <div tabIndex='0' className={ widgetClassName } onClick={ ( event ) => openPopover( event.currentTarget ) }>
            
            {/* placeholder */}
            <div className='ui-date-range-picker__placeholder'>{ props.placeholder }</div>

            {/* value */}
            {
                isEmpty( dateRangeValue ) ? null : (
                    <div className='ui-date-range-picker__value'>
                        <div className='ui-date-range-picker__value__dates'>
                            <div className='ui-date-range-picker__value__dates__item'>{ dateRangeValue[ 0 ] }</div>
                            <div className='ui-date-range-picker__value__dates__dash' />
                            <div className='ui-date-range-picker__value__dates__item'>{ dateRangeValue[ 1 ] }</div>
                        </div>

                        {/* arrow */}
                        <div className='ui-date-range-picker__value__arrow'>
                            <Icon name='downArrow' width='14' height='14'/>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

// set display name
dateRangePicker.displayName = 'dateRangePicker';

// set default props
dateRangePicker.defaultProps = {
    value: [],
    placeholder: 'Select Dates...',
    onChange: noop,
    closeOnSelection: true,
    config: {},
    frequency: null
};

// prop types
dateRangePicker.propTypes = {};

/************************************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { dateRangePickerPreloader as DateRangePickerPreloader } from 'ui/views/generic/dateRangePicker';
 *
 * // render
 * <DateRangePickerPreloader propName='propValue'/>
 */
export const dateRangePickerPreloader = ( props ) => {

    // widget class name
    const widgetClassName = classnames( 'ui-date-range-picker-preloader', props.className );

    return (
        <div className={ widgetClassName }>
            
            {/* placeholder */}
            <div className='ui-date-range-picker-preloader__placeholder'/>

            {/* value */}
            <div className='ui-date-range-picker-preloader__value'>
                <div className='ui-date-range-picker-preloader__value__dates'>
                    <div className='ui-date-range-picker-preloader__value__dates__item'/>
                    <div className='ui-date-range-picker-preloader__value__dates__dash' />
                    <div className='ui-date-range-picker-preloader__value__dates__item'/>
                </div>

                {/* arrow */}
                <div className='ui-date-range-picker-preloader__value__arrow' />
            </div>
        </div>
    );
};

// set display name
dateRangePickerPreloader.displayName = 'dateRangePickerPreloader';

// set default props
dateRangePickerPreloader.defaultProps = {
    value: [],
    placeholder: 'Select Dates...',
    onChange: noop,
};

// prop types
dateRangePickerPreloader.propTypes = {};
